/**
 * AEM's App Integration Framework already renders `<app-root>` with its config attributes
 * (`data-base`, `customconfiguration`, `contextconfiguration`, …) baked in — there is no separate
 * wrapper element to query, and no `<app-root>` to create; Angular's `bootstrapApplication` finds
 * this exact element on its own once the main bundle runs. The AEM dialog only lets authors point
 * at a single non-module script URL though, not a `<script type="module">` tag, so this classic
 * (non-module) script is the only thing AEM embeds directly. It fetches `entrypoints.json`
 * (produced by `scripts/create-entrypoints.js`) and injects the real module bundles at runtime via
 * the DOM API, where `type="module"` works normally.
 */
import { injectLink, injectScript, injectStyle } from "./launcher-helper/load-file";
import { startLoadingScreen, stopLoadingScreen } from "./launcher-helper/loading-screen";
import { APP_READY_EVENT } from "./app-ready-event";

/**
 * If the app never reports ready — a failed bootstrap, a dead API — the placeholder must not sit
 * there for good. Generous on purpose: cutting a slow but working load short is worse than a few
 * extra seconds of spinner.
 */
const READY_TIMEOUT_MS = 15000;

interface Entrypoints {
    main?: string;
    polyfills?: string;
    styles?: string;
    /** Chunks main.js imports statically. Nothing lazy — see create-entrypoints.js. */
    preload?: string[];
}

/**
 * Runs `reveal` once, either when the app reports ready or when the timeout runs out.
 *
 * The two `requestAnimationFrame`s matter: the event fires while Angular is still inside the change
 * detection run that flipped `loaded`, so the app's own markup has not been laid out yet. Swapping
 * in that moment would show an empty host for a frame.
 */
const whenAppReady = (reveal: () => void): void => {
    let done = false;

    const runOnce = (): void => {
        if (done) {
            return;
        }
        done = true;

        requestAnimationFrame(() => requestAnimationFrame(reveal));
    };

    document.addEventListener(APP_READY_EVENT, runOnce, { once: true });
    window.setTimeout(runOnce, READY_TIMEOUT_MS);
};

async function run(): Promise<void> {
    const element = document.querySelector<HTMLElement>("app-root");

    if (!element) {
        console.warn("[rbmaz-launcher]", "no <app-root> found");
        return;
    }

    // Reserves the settled height of the real content (see launch.css) from the very first paint.
    // Deliberately never removed - `app-root` must never go from taller back to shorter, or
    // whatever follows it in the AEM page jumps up at that moment. It also must never be
    // `display: none`: a hidden element has no box, so a min-height on it would do nothing
    // exactly while it matters most.
    element.classList.add("rbmaz-root");
    startLoadingScreen(element);

    whenAppReady(() => {
        stopLoadingScreen();
        console.log("[rbmaz-launcher]", "finished");
    });

    const urlBase = element.getAttribute("data-base") ?? "";

    const resp = await fetch(`${urlBase}spa/entrypoints.json?v=${Date.now().toString()}`);
    const manifest: Entrypoints = await resp.json();

    // Stylesheet first: it does not block the module graph and the app needs it to paint.
    if (manifest.styles) {
        injectStyle(urlBase + "spa/" + manifest.styles);
    }

    // Without these the browser only discovers the shared chunks after parsing main.js. Preloading
    // all of them (not just one) is the fix — see the comment in create-entrypoints.js.
    for (const chunk of manifest.preload ?? []) {
        injectLink({ rel: "modulepreload", href: urlBase + "spa/" + chunk });
    }

    // Zone.js has to be running before main.js executes, so it goes first. `injectScript` clears
    // `async` on both, so per spec they execute in this insertion order once fetched.
    if (manifest.polyfills) {
        injectScript(urlBase + "spa/" + manifest.polyfills);
    }
    if (manifest.main) {
        injectScript(urlBase + "spa/" + manifest.main);
    }
}

void run().catch((err: unknown) => {
    console.error("[rbmaz-launcher]", err);
});
